import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import "./AuthConfirm.scss";

const SUPABASE_URL =
  process.env.REACT_APP_SUPABASE_URL ||
  "https://ixqzjaxnndbbovdpvpnk.supabase.co";

const SUPABASE_ANON_KEY =
  process.env.REACT_APP_SUPABASE_ANON_KEY ||
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Iml4cXpqYXhubmRiYm92ZHB2cG5rIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDE3MjQ3OSwiZXhwIjoyMDU3MzQ4NDc5fQ.dpA1TXDBM0m9yx4QOCSl3APEWhf-q1O1VyGLBGqHUP8";

const ALLOWED_TYPES = new Set([
  "signup",
  "invite",
  "magiclink",
  "recovery",
  "email_change",
  "email",
]);

const NATIVE_CALLBACK = "com.spinninbeans.b2b://auth/callback";

function isNativeCallback(value) {
  return value === NATIVE_CALLBACK;
}

function isIOSLike() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function buildNativeCallback(session, type) {
  const fragment = new URLSearchParams({
    access_token: session.access_token,
    refresh_token: session.refresh_token,
    expires_in: String(session.expires_in || 3600),
    expires_at: String(
      session.expires_at || Math.floor(Date.now() / 1000) + (session.expires_in || 3600)
    ),
    token_type: session.token_type || "bearer",
    type,
  });

  return `${NATIVE_CALLBACK}#${fragment.toString()}`;
}

async function verifyEmailToken(tokenHash, type) {
  const response = await fetch(`${SUPABASE_URL}/auth/v1/verify`, {
    method: "POST",
    headers: {
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      token_hash: tokenHash,
      type,
    }),
  });

  const body = await response.json().catch(() => ({}));

  if (!response.ok || !body.access_token || !body.refresh_token) {
    throw new Error(body.msg || body.error_description || "This sign-in link could not be verified.");
  }

  return body;
}

async function updatePassword(accessToken, password) {
  const response = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
    method: "PUT",
    headers: {
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ password }),
  });

  const body = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(body.msg || body.error_description || "Password could not be updated.");
  }

  return body;
}

export default function AuthConfirm() {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState({
    status: "verifying",
    message: "Checking your sign-in link...",
    nativeUrl: null,
    session: null,
  });
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  const tokenHash = searchParams.get("token_hash");
  const type = searchParams.get("type") || "email";
  const redirectTo = searchParams.get("redirect_to") || "";

  const shouldOpenNative = useMemo(() => {
    return isNativeCallback(redirectTo) || isIOSLike();
  }, [redirectTo]);

  useEffect(() => {
    let isMounted = true;

    async function run() {
      if (!tokenHash || !ALLOWED_TYPES.has(type)) {
        setState({
          status: "error",
          message: "This sign-in link is missing a required token.",
          nativeUrl: null,
        });
        return;
      }

      try {
        const session = await verifyEmailToken(tokenHash, type);
        const nativeUrl = buildNativeCallback(session, type);

        if (!isMounted) return;

        if (type === "recovery") {
          setState({
            status: "needs_password",
            message: "Choose a new password for your Spinnin' Beans account.",
            nativeUrl,
            session,
          });
          return;
        }

        setState({
          status: "success",
          message: shouldOpenNative
            ? "You are verified. Opening Spinnin' Beans..."
            : "You are verified. Open Spinnin' Beans on your iPhone to finish signing in.",
          nativeUrl,
          session: null,
        });

        if (shouldOpenNative) {
          window.location.href = nativeUrl;
        }
      } catch (error) {
        if (!isMounted) return;
        setState({
          status: "error",
          message: error instanceof Error ? error.message : "This sign-in link could not be verified.",
          nativeUrl: null,
          session: null,
        });
      }
    }

    run();

    return () => {
      isMounted = false;
    };
  }, [redirectTo, shouldOpenNative, tokenHash, type]);

  const handlePasswordUpdate = async (event) => {
    event.preventDefault();

    if (!state.session?.access_token) {
      setState((current) => ({
        ...current,
        status: "error",
        message: "This password reset session has expired. Please request a fresh link.",
      }));
      return;
    }

    if (password.length < 8) {
      setState((current) => ({
        ...current,
        message: "Use at least 8 characters for your new password.",
      }));
      return;
    }

    if (password !== passwordConfirmation) {
      setState((current) => ({
        ...current,
        message: "Those passwords do not match yet.",
      }));
      return;
    }

    setIsUpdatingPassword(true);
    try {
      await updatePassword(state.session.access_token, password);
      setPassword("");
      setPasswordConfirmation("");
      setState((current) => ({
        ...current,
        status: "success",
        message: "Password updated. You can open Spinnin' Beans and sign in.",
        session: null,
      }));
    } catch (error) {
      setState((current) => ({
        ...current,
        message: error instanceof Error ? error.message : "Password could not be updated.",
      }));
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  return (
    <section className="auth-confirm-page">
      <div className="auth-confirm-card">
        <img
          className="auth-confirm-logo"
          src="/logo192.png"
          alt="Spinnin' Beans"
        />
        <p className="auth-confirm-kicker">Spinnin' Beans Auth</p>
        <h1>
          {state.status === "verifying" && "Tuning the signal"}
          {state.status === "needs_password" && "Reset password"}
          {state.status === "success" && "You are in"}
          {state.status === "error" && "Link needs a refresh"}
        </h1>
        <p className={`auth-confirm-message ${state.status}`}>{state.message}</p>

        {state.status === "verifying" && (
          <div className="auth-confirm-spinner" aria-label="Verifying" />
        )}

        {state.status === "needs_password" && (
          <form className="auth-confirm-form" onSubmit={handlePasswordUpdate}>
            <label>
              <span>New password</span>
              <input
                type="password"
                autoComplete="new-password"
                value={password}
                minLength={8}
                onChange={(event) => setPassword(event.target.value)}
              />
            </label>
            <label>
              <span>Confirm password</span>
              <input
                type="password"
                autoComplete="new-password"
                value={passwordConfirmation}
                minLength={8}
                onChange={(event) => setPasswordConfirmation(event.target.value)}
              />
            </label>
            <button
              className="auth-confirm-button primary"
              type="submit"
              disabled={isUpdatingPassword}
            >
              {isUpdatingPassword ? "Updating..." : "Update password"}
            </button>
          </form>
        )}

        {state.status === "success" && state.nativeUrl && (
          <div className="auth-confirm-actions">
            <a className="auth-confirm-button primary" href={state.nativeUrl}>
              Open the app
            </a>
            <Link className="auth-confirm-button secondary" to="/dig">
              App info
            </Link>
          </div>
        )}

        {state.status === "error" && (
          <div className="auth-confirm-actions">
            <Link className="auth-confirm-button primary" to="/dig">
              Get a fresh link
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
